import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useInitiateResetPhoneNumberPublicMutation } from 'apollo/queries/autogenerate/hooks-public';
import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import { getParsedPhoneNumber } from 'shared/utils/phone';
import validator, { rules } from 'shared/utils/validator';

type Props = {
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every step requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
  onSuccess: (email: string, phone: string, newPhone: string) => void;
};

const PHONE_NUMBER_ENTRY_REGEXP = /[^0-9]/;

type FormProps = {
  email: string;
  currentPhoneNumber: string;
  newPhoneNumber: string;
  emailOTP: string;
};

const useFormWithValidations = (props: FormProps) => {
  const form = useForm<FormProps>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: 'email',
      },
      {
        validate: validator.getRules(['mustBeValidEmail']),
      }
    );

    form.register(
      {
        name: 'currentPhoneNumber',
      },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );

    form.register(
      {
        name: 'newPhoneNumber',
      },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );

    form.register({
      name: 'emailOTP',
    });
  }, []);

  form.watch();

  return form;
};

const ChangePhoneEnterEmail = (props: Props) => {
  const form = useFormWithValidations({
    email: '',
    currentPhoneNumber: '',
    newPhoneNumber: '',
    emailOTP: '',
  });

  const [samePhoneErrorMessage, setSamePhoneErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>('');

  const [
    initiateResetPhoneNumberPublic,
  ] = useInitiateResetPhoneNumberPublicMutation({
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  const validateEmailAndPhone = async () => {
    const { currentPhoneNumber, newPhoneNumber } = form.getValues();

    const { isError, message } = rules.mustNotBeSame(
      getParsedPhoneNumber(currentPhoneNumber),
      getParsedPhoneNumber(newPhoneNumber),
      '携帯電話番号'
    );

    if (isError) {
      setSamePhoneErrorMessage(message);
    } else {
      setSamePhoneErrorMessage('');
    }

    await form.triggerValidation([
      'email',
      'currentPhoneNumber',
      'newPhoneNumber',
    ]);
  };

  // Sends an OTP code to user's email.
  // Can be invoked to request a new email OTP.
  const initiateResetPhoneNumber = throttle(
    async () => {
      try {
        const { email, currentPhoneNumber, newPhoneNumber } = form.getValues();

        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        await initiateResetPhoneNumberPublic({
          variables: {
            email,
            currentPhoneNumber: getParsedPhoneNumber(currentPhoneNumber),
            recaptchaPlatform: props.reCaptchaPlatform,
            recaptchaToken: reCaptchaToken,
          },
        });

        props.onSuccess(email, currentPhoneNumber, newPhoneNumber);
      } catch (err) {
        setErrorMessage('入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  const checkValidities = (): boolean => {
    const { currentPhoneNumber, newPhoneNumber, email } = form.getValues();

    if (!email || !currentPhoneNumber || !newPhoneNumber) {
      return true;
    }

    if (
      rules.mustBeValidEmail(email) !== true ||
      rules.mustBeValidPhone(currentPhoneNumber) !== true ||
      rules.mustBeValidPhone(newPhoneNumber) !== true
    ) {
      return true;
    }

    const { isError } = rules.mustNotBeSame(
      getParsedPhoneNumber(currentPhoneNumber),
      getParsedPhoneNumber(newPhoneNumber),
      '携帯電話番号'
    );

    if (isError) {
      return true;
    }

    if (form.errors.newPhoneNumber?.message) {
      setErrorMessage('');
      setSamePhoneErrorMessage('');
      form.clearError('newPhoneNumber');
      form.triggerValidation('newPhoneNumber');
    }

    return false;
  };

  return (
    <>
      <View style={style.topView}>
        <View style={style.flexOne}>
          <View style={style.spacer} />
          <TextInput
            label="現在のメールアドレス"
            placeholder="hello@paidy.com"
            value={form.getValues('email')}
            onChangeText={(text) => {
              form.setValue('email', text);
            }}
            onBlur={() => {
              setErrorMessage('');
              form.clearError('email');
              form.triggerValidation('email');
              validateEmailAndPhone();
            }}
            message={
              (form.errors.email?.message as string) || errorMessage || ''
            }
            state={
              form.errors.email?.message || errorMessage ? 'error' : undefined
            }
            id="edit-user-phone-current-email-field"
          />
          <View style={style.spacer} />
        </View>
        <View style={style.flexOne}>
          <View style={style.spacer} />
          <TextInput
            placeholder="09000000000"
            label="現在の携帯電話番号"
            keyboardType="phone-pad"
            textContentType="telephoneNumber"
            onChangeText={(text) => {
              // Allow only digits
              if (text.match(PHONE_NUMBER_ENTRY_REGEXP) !== null) {
                return;
              }

              form.setValue('currentPhoneNumber', text);
            }}
            value={form.getValues('currentPhoneNumber')}
            onBlur={() => {
              setErrorMessage('');
              form.clearError('currentPhoneNumber');
              form.triggerValidation('currentPhoneNumber');
              validateEmailAndPhone();
            }}
            message={
              (form.errors.currentPhoneNumber?.message as string) ||
              errorMessage ||
              ''
            }
            state={
              form.errors.currentPhoneNumber?.message || errorMessage
                ? 'error'
                : undefined
            }
            id="edit-user-phone-current-phone-field"
          />
          <View style={style.spacer} />
        </View>
        <View style={style.flexOne}>
          <View style={style.spacer} />
          <TextInput
            placeholder="09000000000"
            label="新しい携帯電話番号"
            keyboardType="phone-pad"
            textContentType="telephoneNumber"
            onChangeText={(text) => {
              // Allow only digits
              if (text.match(PHONE_NUMBER_ENTRY_REGEXP) !== null) {
                return;
              }

              form.setValue('newPhoneNumber', text);
            }}
            value={form.getValues('newPhoneNumber')}
            onBlur={() => {
              setErrorMessage('');
              setSamePhoneErrorMessage('');
              form.clearError('newPhoneNumber');
              form.triggerValidation('newPhoneNumber');
              validateEmailAndPhone();
            }}
            message={
              (form.errors.newPhoneNumber?.message as string) ||
              errorMessage ||
              samePhoneErrorMessage ||
              ''
            }
            state={
              form.errors.newPhoneNumber?.message ||
              errorMessage ||
              samePhoneErrorMessage
                ? 'error'
                : undefined
            }
            id="edit-user-phone-new-phone-field"
          />
          <View style={style.spacer} />
        </View>
      </View>
      <View style={style.buttonView}>
        <Button
          title="次へ"
          onPress={initiateResetPhoneNumber}
          style={style.button}
          id="edit-user-phone-next-button"
          disabled={checkValidities()}
        />
      </View>
    </>
  );
};

const style = StyleSheet.create({
  spacer: {
    marginBottom: 5,
  },
  button: {
    maxWidth: 296,
    alignSelf: 'center',
    marginVertical: 8,
  },
  topView: {
    flex: 1,
  },
  buttonView: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  flexOne: {
    flex: 1,
  },
});

export default ChangePhoneEnterEmail;
